/**
 * Debounced persistent storage for Zustand `persist` middleware.
 *
 * Zustand's default persist writes synchronously (JSON.stringify +
 * localStorage.setItem) on EVERY set() call. Stores that receive
 * per-pointermove updates (tool state, ruler cursor tracking, etc.) pay that
 * cost on every frame even when `partialize` trims the payload.
 *
 * This wrapper keeps hydration reads synchronous (localStorage is required at
 * startup) but coalesces writes into a trailing debounce, flushing pending
 * data on page hide/unload so nothing is lost when the window closes.
 */

const FLUSH_DELAY_MS = 400;

interface PendingEntry {
    timer: ReturnType<typeof setTimeout>;
    serialized: string;
}

const pending = new Map<string, PendingEntry>();

const flushKey = (key: string) => {
    const entry = pending.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.delete(key);
    try {
        localStorage.setItem(key, entry.serialized);
    } catch {
        // Quota/private-mode failures must never break the store update
    }
};

const flushAll = () => {
    for (const key of Array.from(pending.keys())) flushKey(key);
};

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushAll);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushAll();
    });
}

export const debouncedLocalStorage = (): Storage => ({
    get length() {
        return localStorage.length;
    },
    clear: () => {
        for (const key of Array.from(pending.keys())) {
            clearTimeout(pending.get(key)!.timer);
        }
        pending.clear();
        localStorage.clear();
    },
    getItem: (key: string) => localStorage.getItem(key),
    key: (index: number) => localStorage.key(index),
    removeItem: (key: string) => {
        const entry = pending.get(key);
        if (entry) {
            clearTimeout(entry.timer);
            pending.delete(key);
        }
        localStorage.removeItem(key);
    },
    setItem: (key: string, value: string) => {
        const existing = pending.get(key);
        if (existing) clearTimeout(existing.timer);
        const timer = setTimeout(() => flushKey(key), FLUSH_DELAY_MS);
        pending.set(key, { timer, serialized: value });
    },
});
